import { useState } from 'react';
import { DatePicker, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useLazyExportProvisionalSalesXlsxQuery, useProvisionalSalesReportQuery } from '@/entities/reports';
import type { ProvisionalSalesReportRow } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';

/** §13, §8.6: строки с предварительной себестоимостью за период. */
export function ProvisionalSalesReportPage() {
  const ctx = useStoreContext();
  const today = todayInTz(ctx.timezone);
  const [from, setFrom] = useState(dayjs(today).startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(today);
  const { data, isFetching } = useProvisionalSalesReportQuery({ from, to }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportProvisionalSalesXlsxQuery();

  const columns: ColumnsType<ProvisionalSalesReportRow> = [
    { title: 'Документ', dataIndex: 'typeLabel', width: 180 },
    { title: 'Номер', dataIndex: 'number', width: 110 },
    { title: 'Дата', dataIndex: 'docDate', width: 110, render: (v: string) => formatDate(v) },
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Штук', dataIndex: 'qtyPcs', width: 90, align: 'right' },
    { title: 'Без партии, шт', dataIndex: 'qtyUncovered', width: 130, align: 'right' },
    { title: 'Себестоимость', dataIndex: 'costAmount', width: 130, align: 'right', render: (v: string | null) => v ?? '—' },
    {
      title: '',
      width: 160,
      render: (_v, r) => (r.noPurchasePrice ? <Tag color="error">нет закупочной цены</Tag> : <Tag color="warning">предварительная</Tag>),
    },
  ];

  return (
    <ReportPage
      title="Продажи с предварительной себестоимостью"
      filters={
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          value={[dayjs(from), dayjs(to)]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setFrom(dates[0].format('YYYY-MM-DD'));
              setTo(dates[1].format('YYYY-MM-DD'));
            }
          }}
        />
      }
      columns={columns}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey={(r) => `${r.documentId}-${r.categoryId}`}
      onExport={() => exportXlsx({ from, to }).unwrap()}
      exportFilename="Предварительная себестоимость.xlsx"
    />
  );
}
